import { Box, Group, List, Paper, Stack, Text, ThemeIcon } from '@mantine/core'
import { FileRejection } from '@mantine/dropzone'
import { IconAlertTriangle, IconCheck, IconX } from '@tabler/icons-react'
import { FormattedMessage } from 'react-intl'
import { MEDIA_UPLOAD_LIMITS } from '@/config/media'
import { bytesToGigabytes, formatSize } from '@/utils/file'

const MAX_DISPLAYED_FILES = 10

type RejectionCode =
  | 'file-invalid-type'
  | 'file-too-large'
  | 'file-too-small'
  | 'name-too-long'
  | 'batch-size-exceeded'
  | 'too-many-files'

type RejectionReasonProps = {
  code: RejectionCode
}

const RejectionReason = ({ code }: RejectionReasonProps) => {
  if (code === 'file-invalid-type') {
    return <FormattedMessage id="datasets.upload.rejection.invalidFileType" />
  }

  if (code === 'file-too-large') {
    return (
      <FormattedMessage
        id="datasets.upload.rejection.fileTooLarge"
        values={{
          maxSize:
            bytesToGigabytes(MEDIA_UPLOAD_LIMITS.MAX_FILE_SIZE_BYTES) + ' GB'
        }}
      />
    )
  }

  if (code === 'name-too-long') {
    return (
      <FormattedMessage
        id="datasets.upload.rejection.nameTooLong"
        values={{
          limit: MEDIA_UPLOAD_LIMITS.MAX_FILE_NAME_LENGTH
        }}
      />
    )
  }

  if (code === 'batch-size-exceeded') {
    return (
      <FormattedMessage
        id="datasets.upload.rejection.batchSizeExceeded"
        values={{
          maxSize:
            bytesToGigabytes(MEDIA_UPLOAD_LIMITS.MAX_BATCH_SIZE_BYTES) + ' GB'
        }}
      />
    )
  }

  if (code === 'too-many-files') {
    return (
      <FormattedMessage
        id="datasets.upload.rejection.tooManyFiles"
        values={{
          limit: MEDIA_UPLOAD_LIMITS.MAX_FILES_PER_BATCH
        }}
      />
    )
  }

  return ''
}

type ConfirmFileListProps = {
  acceptedFiles: File[]
  fileRejections: FileRejection[]
}

export const ConfirmFileList = ({
  acceptedFiles,
  fileRejections
}: ConfirmFileListProps) => {
  const acceptedFileCount = acceptedFiles.length
  const rejectedFileCount = fileRejections.length

  const rejectionsByCode = fileRejections.reduce(
    (acc, rejection) => {
      const code = rejection.errors[0].code as RejectionCode
      const existing = acc[code] || []

      return {
        ...acc,
        [code]: [...existing, rejection.file]
      }
    },
    {} as Record<RejectionCode, File[]>
  )

  const rejectionsByCodeEntries = Object.entries(rejectionsByCode)
  const showCodeCount = rejectionsByCodeEntries.length > 1

  const acceptedTotalSize = acceptedFiles.reduce(
    (acc, file) => acc + file.size,
    0
  )

  return (
    <Stack gap="xl">
      {acceptedFileCount > 0 && (
        <Box>
          <Group wrap="nowrap" gap="xs" mb="sm">
            <ThemeIcon color="green" size={16}>
              <IconCheck />
            </ThemeIcon>

            <Text size="sm">
              <FormattedMessage
                id="datasets.upload.readyForUpload"
                values={{ count: acceptedFileCount }}
              />
            </Text>

            <Text size="sm" c="dimmed">
              ({formatSize(acceptedTotalSize, 'b', true)})
            </Text>
          </Group>

          <Paper bg="gray.1" p="xs">
            <List size="xs">
              {acceptedFiles
                .slice(0, MAX_DISPLAYED_FILES)
                .map((acceptedFile) => (
                  <List.Item key={acceptedFile.name} icon={null}>
                    <Text size="xs">{acceptedFile.name}</Text>
                  </List.Item>
                ))}
            </List>

            {acceptedFiles.length > MAX_DISPLAYED_FILES && (
              <Text size="xs" mt="xs" fw={600}>
                <FormattedMessage
                  id="datasets.upload.readyForUpload.more"
                  values={{
                    count: acceptedFiles.length - MAX_DISPLAYED_FILES
                  }}
                />
              </Text>
            )}
          </Paper>
        </Box>
      )}

      {rejectedFileCount > 0 && (
        <Box>
          <Group wrap="nowrap" gap="xs" mb="sm">
            <ThemeIcon color="red" size={16}>
              <IconX />
            </ThemeIcon>

            <Text size="sm">
              <FormattedMessage
                id="datasets.upload.rejectedFiles"
                values={{ count: rejectedFileCount }}
              />
            </Text>
          </Group>

          <Paper bg="gray.1" p="xs">
            <List size="xs" center>
              {rejectionsByCodeEntries.map(([code, files]) => (
                <List.Item key={code} icon={<IconAlertTriangle size={16} />}>
                  <Group wrap="nowrap" gap="xs">
                    <Text size="xs">
                      <RejectionReason code={code as RejectionCode} />
                    </Text>

                    {showCodeCount && (
                      <Text size="xs" c="dimmed">
                        ({files.length})
                      </Text>
                    )}
                  </Group>
                </List.Item>
              ))}
            </List>
          </Paper>
        </Box>
      )}
    </Stack>
  )
}
